/**
 * React Context Provider for SSO
 */

import React, { createContext, useMemo, useState, useEffect } from 'react';
import { Kit19SSOClient } from './ssoClient';

export const SSOContext = createContext(null);

/**
 * SSO Provider Component
 * Wrap your app with this to enable SSO functionality
 */
export const SSOProvider = ({ children, config }) => {
  const ssoClient = useMemo(() => new Kit19SSOClient(config), [config]);
  const [isAuthenticated, setIsAuthenticated] = useState(() => ssoClient.isAuthenticated());
  const [currentUser, setCurrentUser] = useState(() => ssoClient.getCurrentUser());
  
  // Update auth state when storage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const authStatus = ssoClient.isAuthenticated();
      const user = ssoClient.getCurrentUser();
      
      // Only update if values actually changed
      setIsAuthenticated(prev => prev !== authStatus ? authStatus : prev);
      setCurrentUser(prev => {
        if (!prev && !user) return prev;
        if (!prev || !user) return user;
        if (prev.userId !== user.userId) return user;
        return prev;
      });
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Check less frequently - every 5 seconds instead of 1
    const interval = setInterval(handleStorageChange, 5000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [ssoClient]);
  
  const value = {
    ssoClient,
    isAuthenticated,
    currentUser,
    login: (customConfig) => ssoClient.login(customConfig),
    logout: (returnUrl) => {
      ssoClient.logout(returnUrl);
      setIsAuthenticated(false);
      setCurrentUser(null);
    },
    refreshAuth: () => {
      setIsAuthenticated(ssoClient.isAuthenticated());
      setCurrentUser(ssoClient.getCurrentUser());
    }
  };
  
  return (
    <SSOContext.Provider value={value}>
      {children}
    </SSOContext.Provider>
  );
};

export default SSOProvider;
